<template>
    <transition-group name="invitations" tag="div">

        <aside v-if="isStage('invitations')" key="invitations" class="invitations lc-invite-users">
            <header class="panel__header">
                <h2 class="panel__title"><slot name="invite_users_title">Invite people</slot></h2>
            </header>

            <form @submit.prevent="validate" action="#" autocomplete="off">
                <div class="invitation" v-for="(invite, index) in form.invites" :key="invite.id" :class="{ 'form__group--error' : form.errors.has(`invites.${index}.email`)}">
                    <input type="text"
                           name="user"
                           placeholder="john@example.com"
                           autocomplete="off"
                           class="lc-input form__input lookup__input"
                           :class="`lc-user-${index + 1}`"
                           v-model="invite.email"
                           ref="input"/>
                    <a v-if="form.invites.length > 1" @click.prevent="remove(index)" href="#" class="invitation__icon icon-button" v-tooltip="`Remove`">
                        <icon type="close" color="danger" size="18"></icon>
                    </a>
                    <p v-if="form.errors.has(`invites.${index}.email`)" class="form__help form__help--error">{{ form.errors.get(`invites.${index}.email`) }}</p>
                </div>
                <a @click.prevent="addInvite" href="#" class="lc-add-another invitations__link">+ add another</a>
                <div class="form__group" :class="{ 'form__group--error' : form.errors.has('message')}">
                    <label for="message" class="form__label">Personalised message</label>
                    <textarea id="message" name="message" class="lc-message form__input" cols="60" rows="3" v-model="form.message"></textarea>
                    <p v-if="form.errors.has('message')" class="form__help form__help--error">{{ form.errors.get('message') }}</p>
                </div>
                <div class="form__group" v-if="channels.length > 0" :class="{ 'form__group--error' : form.errors.has('channels')}">
                    <h2 class="panel__title">To channel</h2>
                    <label :key="channel.id" v-for="channel in channels" class="form__label">
                        <input :type="role === 'single_channel_guest' ? 'radio': 'checkbox'" v-model="form.channels" :value="channel.id">
                        {{ channel.name }}
                    </label>
                </div>
                <p v-if="form.errors.has('channels')" class="form__help form__help--error">{{ form.errors.get('channels') }}</p>

                <footer class="invitations__footer">
                    <a @click.prevent="$emit('back')" href="#" class="button button--small button--link">Cancel</a>
                    <button @click="validate" class="lc-invite button button--small button--primary">Invite</button>
                </footer>
            </form>
        </aside>

        <aside v-if="isStage('confirmation')" key="confirmation" class="lc-confirm-invitations invitations">
            <article class="box confirmation">
                <header class="confirmation__header">
                    <svg width="64px" height="64px" viewBox="0 0 64 64" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g id="Page-1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                            <g id="noun_confirmation_1581270" fill="#1C89FF" fill-rule="nonzero">
                                <path d="M32,8.12048841e-15 C14.326888,2.28388736e-15 2.28388736e-15,14.326888 0,32 C-2.28388736e-15,49.673112 14.326888,64 32,64 C49.673112,64 64,49.673112 64,32 C63.9811033,14.3347212 49.6652788,0.0188967309 32,8.12048841e-15 Z M32,59.4285714 C16.8516183,59.4285714 4.57142857,47.1483817 4.57142857,32 C4.57142857,16.8516183 16.8516183,4.57142857 32,4.57142857 C47.1483817,4.57142857 59.4285714,16.8516183 59.4285714,32 C59.4121946,47.141593 47.141593,59.4121946 32,59.4285714 Z"
                                      id="Shape"></path>
                                <path d="M46.384,18.9554286 L25.1428571,40.1965714 L17.616,32.6697143 C17.0421155,32.0755278 16.1922791,31.8372283 15.3931261,32.0464065 C14.5939731,32.2555846 13.9698704,32.8796874 13.7606922,33.6788404 C13.5515141,34.4779934 13.7898135,35.3278298 14.384,35.9017143 L23.5268571,45.0445714 C24.4194282,45.936873 25.8662861,45.936873 26.7588571,45.0445714 L49.616,22.1874286 C50.4822222,21.2905625 50.469834,19.8649491 49.5881567,18.9832718 C48.7064795,18.1015946 47.280866,18.0892064 46.384,18.9554286 Z"
                                      id="Path"></path>
                            </g>
                        </g>
                    </svg>
                    <h4 class="confirmation__title">Confirmation</h4>
                </header>
                <p class="confirmation__label"><slot name="confirmation_label">To be invited</slot></p>
                <p v-for="invite in form.invites" :key="invite.id" class="confirmation__item">
                    <strong>{{ invite.email }}</strong>
                </p>
                <template v-if="selectedChannels.length > 0">
                    <p class="confirmation__label">To channels</p>
                    <p v-for="channel in selectedChannels" :key="channel.id" class="confirmation__item">
                        <strong>{{ channel.name }}</strong>
                    </p>
                </template>
                <span class="confirmation__divider"></span>
                <p class="confirmation__label">Your message</p>
                <p class="confirmation__item">{{ form.message }}</p>
            </article>
            <footer class="invitations__footer">
                <a @click.prevent="goToStage('invitations')" href="#" class="button button--small button--link">Cancel</a>
                <button @click="confirm" :disabled="isBusy || form.busy" class="lc-invite button button--small button--primary">Confirm</button>
            </footer>
        </aside>

    </transition-group>
</template>

<script>
/* global EventBus */
import uuid from 'uuid/v4'
import Form from '../../../helpers/Form'
import Icon from '../Icon'

export default {
    props: {
        message: {
            required: true,
            type: String
        },

        action: {
            required: true,
            type: String
        },

        role: {
            type: String,
            default: 'single_chanel_role'
        },

        channels: {
            required: false,
            type: Array,
            default () {
                return []
            }
        }
    },

    data () {
        return {
            isBusy: false,
            stage: 'invitations',
            form: new Form({
                force: false,
                role: this.role,
                message: '',
                invites: [],
                channels: []
            })
        }
    },

    created () {
        this.form.message = this.message
        this.addInvite()
    },

    methods: {
        addInvite () {
            this.form.invites.push({ id: uuid(), email: '' })
        },

        remove (index) {
            this.form.invites.splice(index, 1)

            this.form.errors.forget()
        },

        isStage (stage) {
            return this.stage === stage
        },

        goToStage (stage) {
            this.stage = stage
        },

        validate () {
            this.$store.dispatch(this.action, this.form).then(() => {
                this.goToStage('confirmation')
            })
        },

        confirm () {
            if (this.isBusy) {
                return
            }

            this.isBusy = true
            this.form.force = true

            this.$store.dispatch(this.action, this.form).then(() => {
                this.$emit('back')
                EventBus.$emit('newSnackbar', 'Invitations sent')
            }).finally(() => {
                this.isBusy = false
                this.form.force = false
            })
        }
    },

    computed: {
        selectedChannels () {
            let channels = this.form.channels instanceof Array ? this.form.channels : [this.form.channels]

            return this.channels.filter(channel => channels.includes(channel.id))
        }
    },

    components: {
        Icon
    }
}
</script>
